import { useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { emailEditorModules } from "shared/config/quillSetup";
import { validateFileForUpload, formatFileSize, FILE_INPUT_ACCEPT } from "shared/utils/fileValidation";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
} from "@heroui/react";
import { X, PaperPlaneRight, FileText, Paperclip, File, Trash, ClipboardText } from "@phosphor-icons/react";
import { HelpdeskTicketInterface } from "shared/types/helpdesk";
import { getPrimaryTicketSupplier } from "../../shared/ticketSuppliers";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { AiReplySuggestionPanel } from "shared/components/email/AiReplySuggestionPanel";
import { replySuggestionToHtml } from "shared/utils/replySuggestionHtml";
import { useAiReplySuggestion } from "shared/hooks/useAiReplySuggestion";
import { HELPDESK_DEFAULT_AI_INSTRUCTION } from "shared/utils/aiReplyInstructionDefaults";
import {
  AiReplySuggestion,
  closeHelpdeskReplySuggestion,
  fetchHelpdeskReplySuggestion,
  generateHelpdeskReplySuggestion,
} from "shared/api/ai-reply-suggestion";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";

interface QuickReplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: HelpdeskTicketInterface;
  onSuccess?: () => void;
  replyToSubject?: string;
  replyToBody?: string;
  replyToFrom?: string;
  replyToDate?: string;
  replyToMessageId?: string;
  isInsurance?: boolean;
  insuranceIssueId?: number;
  insuranceRecipients?: string[]; // computed from selected contacts in EmailModal
  communicationMode?: 'tenant' | 'supplier';
  hasExistingEmails?: boolean;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
}

interface AttachedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  uploadProgress?: number;
  isUploading?: boolean;
  uploadError?: string;
  previewUrl?: string;
}

const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: "request_photos",
    name: "Vraag voor extra foto's",
    subject: "Extra foto's nodig - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Bedankt voor uw melding betreffende: <strong>{ticketTitle}</strong></p>

<p>Om uw probleem beter te kunnen beoordelen, zouden we graag wat extra foto's ontvangen van de situatie. Kunt u ons enkele duidelijke foto's doorsturen die het probleem goed in beeld brengen?</p>

<p>U kunt de foto's rechtstreeks beantwoorden op deze e-mail.</p>

<p>Alvast bedankt voor uw medewerking!</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  },
  {
    id: "schedule_visit",
    name: "Afspraak maken voor bezoek",
    subject: "Afspraak voor bezoek - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Bedankt voor uw melding betreffende: <strong>{ticketTitle}</strong></p>

<p>Een van onze medewerkers zou graag langskomen om de situatie ter plaatse te bekijken. We stellen voor om langs te komen op <strong>[DATUM EN TIJDSTIP IN TE VULLEN]</strong>.</p>

<p>Bent u op dat moment thuis? Zo niet, laat ons dan weten wanneer het u beter uitkomt.</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  },
  {
    id: "request_checks",
    name: "Vraag om controles uit te voeren",
    subject: "Controle verzoek - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Bedankt voor uw melding betreffende: <strong>{ticketTitle}</strong></p>

<p>Om uw probleem beter te kunnen analyseren, zouden we u willen vragen om volgende zaken na te kijken en ons per kerende mail uw bevindingen door te geven:</p>

<ul>
<li>Controleer of alle stopcontacten in de betreffende ruimte nog werken</li>
<li>Kijk na of de hoofdschakelaar nog aan staat</li>
<li>Controleer of er zichtbare schade is aan bedrading of apparaten</li>
<li>Noteer wanneer het probleem voor het eerst optrad</li>
</ul>

<p>Deze informatie helpt ons om het probleem sneller en efficiënter op te lossen.</p>

<p>Alvast bedankt voor uw medewerking!</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  },
  {
    id: "phone_followup",
    name: "Telefonische opvolging",
    subject: "Opvolging telefoongesprek - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Zoals telefonisch besproken betreffende uw melding: <strong>{ticketTitle}</strong></p>

<p>Hierbij de afspraken die we hebben gemaakt:</p>

<ul>
<li>[AFSPRAAK 1 - AAN TE VULLEN]</li>
<li>[AFSPRAAK 2 - AAN TE VULLEN]</li>
</ul>

<p>Mocht u nog vragen hebben, aarzel dan niet om contact met ons op te nemen.</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  },
  {
    id: "contractor_scheduled",
    name: "Aannemer ingepland",
    subject: "Interventie ingepland - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Bedankt voor uw geduld betreffende uw melding: <strong>{ticketTitle}</strong></p>

<p>We hebben een aannemer kunnen inplannen voor <strong>[DATUM EN TIJDSTIP IN TE VULLEN]</strong>. De aannemer zal contact met u opnemen om de exacte timing af te stemmen.</p>

<p>Gelieve ervoor te zorgen dat u of iemand anders aanwezig is om de aannemer toegang te verlenen.</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  },
  {
    id: "temporary_solution",
    name: "Tijdelijke oplossing",
    subject: "Tijdelijke oplossing - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Betreffende uw melding: <strong>{ticketTitle}</strong></p>

<p>We hebben een tijdelijke oplossing voorzien om het ongemak te beperken. Een definitieve oplossing wordt zo snel mogelijk uitgewerkt.</p>

<p>Instructies voor de tijdelijke oplossing:</p>
<ul>
<li>[INSTRUCTIE 1 - AAN TE VULLEN]</li>
<li>[INSTRUCTIE 2 - AAN TE VULLEN]</li>
</ul>

<p>Mocht de tijdelijke oplossing niet werken of mocht u andere problemen ondervinden, neem dan onmiddellijk contact met ons op.</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  },
  {
    id: "parts_ordered",
    name: "Onderdelen besteld",
    subject: "Onderdelen besteld - Ticket #{ticketId}",
    content: `<p>Beste {reporterName},</p>

<p>Betreffende uw melding: <strong>{ticketTitle}</strong></p>

<p>We hebben de nodige onderdelen besteld om uw probleem op te lossen. De verwachte levertijd is <strong>[LEVERTIJD IN TE VULLEN]</strong>.</p>

<p>Zodra de onderdelen zijn geleverd, zullen we contact met u opnemen om een afspraak in te plannen voor de reparatie.</p>

<p>Bedankt voor uw geduld.</p>

<p>Met vriendelijke groeten,<br/>
Het Yally team</p>`
  }
];

// Templates specifiek voor verzekeringsdossiers
const EMAIL_TEMPLATES_INSURANCE: EmailTemplate[] = [
  {
    id: "ins_open_case",
    name: "Dossier geopend",
    subject: "Verzekeringsdossier INS{issueId} - {issueTitle}",
    content: `<p>Beste,</p>

<p>We hebben een verzekeringsdossier geopend met referentie <strong>INS{issueId}</strong> voor: <strong>{issueTitle}</strong>.</p>

<p>Locatie: <strong>{buildingId}</strong>{unitIfAny}</p>

<p>We houden u via dit dossier op de hoogte van de verdere stappen.</p>

<p>Met vriendelijke groeten,<br/>Yally</p>`
  },
  {
    id: "ins_request_info",
    name: "Aanvullende info nodig",
    subject: "Aanvullende info - Verzekeringsdossier INS{issueId}",
    content: `<p>Beste,</p>

<p>Voor <strong>INS{issueId}</strong> ontvangen we graag volgende bijkomende informatie of documenten:</p>
<ul>
<li>[Document of info 1]</li>
<li>[Document of info 2]</li>
<li>[Document of info 3]</li>
</ul>

<p>Alvast bedankt voor de snelle terugkoppeling.</p>

<p>Met vriendelijke groeten,<br/>Yally</p>`
  },
  {
    id: "ins_scheduling_expert",
    name: "Expert/bezoek inplannen",
    subject: "Inplanning expertise - Verzekeringsdossier INS{issueId}",
    content: `<p>Beste,</p>

<p>Naar aanleiding van <strong>INS{issueId}</strong> willen we graag een expertise of bezoek inplannen op <strong>[DATUM EN TIJD]</strong>.</p>

<p>Laat even weten of dit moment past, of geef alternatieven door.</p>

<p>Met vriendelijke groeten,<br/>Yally</p>`
  },
  {
    id: "ins_update",
    name: "Statusupdate",
    subject: "Update - Verzekeringsdossier INS{issueId}",
    content: `<p>Beste,</p>

<p>Een korte update over <strong>INS{issueId}</strong>:</p>
<ul>
<li>[Korte statuspunt 1]</li>
<li>[Korte statuspunt 2]</li>
</ul>

<p>We volgen verder op en houden u op de hoogte.</p>

<p>Met vriendelijke groeten,<br/>Yally</p>`
  }
];

// Templates voor leveranciers/aannemers
const EMAIL_TEMPLATES_SUPPLIER: EmailTemplate[] = [
  {
    id: "supplier_ai_summary",
    name: "AI Samenvatting (Automatisch)",
    subject: "Helpdesk ticket HS{ticketId} - {ticketTitle}",
    content: `<p><em>Deze inhoud wordt automatisch gegenereerd door AI op basis van de ticket informatie...</em></p>`,
  },
  {
    id: "supplier_quote_request",
    name: "Offerte aanvraag",
    subject: "Offerte aanvraag - Ticket HS{ticketId}",
    content: `<p>Beste leverancier,</p>

<p>Graag vragen we een offerte aan voor de volgende werkzaamheden:</p>

<p><strong>Ticket:</strong> HS{ticketId} - {ticketTitle}</p>
<p><strong>Locatie:</strong> {buildingAddress}{unitInfo}</p>
<p><strong>Beschrijving:</strong> {ticketDescription}</p>

<p>Kunt u ons een gedetailleerde offerte opsturen inclusief:</p>
<ul>
<li>Materiaalkosten</li>
<li>Arbeidskosten</li>
<li>Verwachte uitvoeringstijd</li>
<li>Beschikbaarheid</li>
</ul>

<p>Met vriendelijke groeten,<br/>
Yally Helpdesk Team</p>`,
  },
  {
    id: "supplier_schedule_work",
    name: "Werk inplannen",
    subject: "Werk inplannen - Ticket HS{ticketId}",
    content: `<p>Beste leverancier,</p>

<p>We willen graag de werkzaamheden voor ticket HS{ticketId} inplannen.</p>

<p><strong>Locatie:</strong> {buildingAddress}{unitInfo}</p>
<p><strong>Werkzaamheden:</strong> {ticketTitle}</p>

<p>Kunt u ons laten weten wanneer u beschikbaar bent? We geven de voorkeur aan:</p>
<ul>
<li>[VOORKEURSDATUM/TIJD IN TE VULLEN]</li>
<li>[ALTERNATIEVE DATUM/TIJD IN TE VULLEN]</li>
</ul>

<p>Contactgegevens ter plaatse: [IN TE VULLEN]</p>

<p>Met vriendelijke groeten,<br/>
Yally Helpdesk Team</p>`,
  },
  {
    id: "supplier_follow_up",
    name: "Opvolging werkzaamheden",
    subject: "Opvolging - Ticket HS{ticketId}",
    content: `<p>Beste leverancier,</p>

<p>We volgen graag op betreffende de werkzaamheden voor ticket HS{ticketId}.</p>

<p>Kunt u ons een update geven over:</p>
<ul>
<li>De huidige status van de werkzaamheden</li>
<li>Eventuele problemen of vertragingen</li>
<li>Verwachte datum van afronding</li>
</ul>

<p>Met vriendelijke groeten,<br/>
Yally Helpdesk Team</p>`,
  },
  {
    id: "supplier_work_completed",
    name: "Bevestiging afronding",
    subject: "Werkzaamheden afgerond? - Ticket HS{ticketId}",
    content: `<p>Beste leverancier,</p>

<p>Zijn de werkzaamheden voor ticket HS{ticketId} afgerond?</p>

<p>Gelieve ons te bevestigen en eventueel foto's van het uitgevoerde werk door te sturen.</p>

<p>Ook ontvangen we graag:</p>
<ul>
<li>Factuur voor de uitgevoerde werkzaamheden</li>
<li>Garantie informatie (indien van toepassing)</li>
<li>Eventuele aanbevelingen voor onderhoud</li>
</ul>

<p>Met vriendelijke groeten,<br/>
Yally Helpdesk Team</p>`,
  }
];

export const QuickReplyModal: React.FC<QuickReplyModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onSuccess,
  replyToSubject,
  replyToBody,
  replyToFrom,
  replyToDate,
  replyToMessageId,
  isInsurance,
  insuranceIssueId,
  insuranceRecipients,
  communicationMode = 'tenant',
  hasExistingEmails = false
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [aiSummaryLoaded, setAiSummaryLoaded] = useState(false);
  const quillRef = useRef<ReactQuill>(null);
  const { session } = useSession();

  /**
   * Het AI-antwoordvoorstel: het voorstel dat bij een inkomende huurdersmail
   * automatisch gemaakt werd, en de knop om er nu één te laten schrijven.
   *
   * Niet bij een verzekeringsdossier — `ticket.id` is daar het dossiernummer en dus
   * niet het ticket waarover het voorstel zou gaan.
   */
  const audience = communicationMode === 'supplier' ? 'supplier' : 'tenant';
  const ticketId = ticket.id;
  const {
    suggestion,
    isGenerating: isGeneratingSuggestion,
    error: suggestionError,
    instruction: suggestionInstruction,
    setInstruction: setSuggestionInstruction,
    resetInstruction: resetSuggestionInstruction,
    isInstructionModified: isSuggestionInstructionModified,
    requestSuggestion,
    markUsed: markSuggestionUsed,
    dismiss: dismissSuggestion,
  } = useAiReplySuggestion({
    generate: (instructie) =>
      ticketId
        ? generateHelpdeskReplySuggestion(ticketId, audience, session, instructie)
        : Promise.reject(new Error('Geen ticket gevonden om op te antwoorden')),
    loadPending: () =>
      ticketId
        ? fetchHelpdeskReplySuggestion(ticketId, audience, session)
        : Promise.resolve(null),
    close: (voorstel, outcome) =>
      voorstel.id
        ? closeHelpdeskReplySuggestion(voorstel.id, outcome, session)
        : Promise.resolve(),
    defaultInstruction: HELPDESK_DEFAULT_AI_INSTRUCTION,
    enabled: isOpen && !!session && !isInsurance,
    context: { module: 'helpdesk', ticketId, audience },
  });

  /**
   * Zet het voorstel bovenaan het bericht, vóór het geciteerde blok van de mail
   * waarop geantwoord wordt.
   */
  const applySuggestion = (voorstel: AiReplySuggestion) => {
    setContent((huidige) => `${replySuggestionToHtml(voorstel.body)}${huidige || ''}`);
    markSuggestionUsed();
  };
  
  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const currentTotalSize = attachedFiles.reduce((total, file) => total + file.size, 0);

    // Filter and validate files using shared utility
    let runningTotal = currentTotalSize;
    const validFiles = fileArray.filter(file => {
      const error = validateFileForUpload(file, runningTotal);
      if (error) {
        toast.error(error);
        return false;
      }
      runningTotal += file.size;
      return true;
    });
    
    // Create attachment objects
    const newAttachments: AttachedFile[] = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      isUploading: false,
      uploadProgress: 0,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }));
    
    setAttachedFiles(prev => [...prev, ...newAttachments]);
    toast.success(`${validFiles.length} bestand(en) toegevoegd`);
  };

  const handleRemoveFile = (fileId: string) => {
    setAttachedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const getTotalAttachmentSize = (): number => {
    return attachedFiles.reduce((total, file) => total + file.size, 0);
  };

  const replaceTemplateVariables = (text: string): string => {
    if (isInsurance) {
      const issueId = (insuranceIssueId || ticket.id)?.toString() || 'N/A';
      const unitId = ticket.unit?.id ? ticket.unit.id : '';
      const unitIfAny = unitId ? `, unit <strong>${unitId}</strong>` : '';
      return text
        .replace(/{issueId}/g, issueId)
        .replace(/{issueTitle}/g, ticket.title || 'Uw dossier')
        .replace(/{buildingId}/g, ticket.building?.id || 'het gebouw')
        .replace(/{unitId}/g, unitId || '')
        .replace(/{unitIfAny}/g, unitIfAny);
    }
    
    // Common variables for both tenant and supplier communication
    const buildingAddress = (ticket.building as any)?.googlePlaceLongAddress || ticket.building?.id || 'Onbekend adres';
    const unitInfo = ticket.unit?.id ? ` - Unit ${ticket.unit.id}` : '';
    
    if (communicationMode === 'supplier') {
      return text
        .replace(/{ticketId}/g, ticket.id?.toString() || 'N/A')
        .replace(/{ticketTitle}/g, ticket.title || 'Onderhoudstaak')
        .replace(/{ticketDescription}/g, ticket.description || 'Geen beschrijving beschikbaar')
        .replace(/{buildingAddress}/g, buildingAddress)
        .replace(/{unitInfo}/g, unitInfo)
        .replace(/{supplierName}/g, getPrimaryTicketSupplier(ticket)?.name || 'Leverancier');
    }
    
    return text
      .replace(/{ticketId}/g, ticket.id?.toString() || 'N/A')
      .replace(/{ticketTitle}/g, ticket.title || 'Uw melding')
      .replace(/{reporterName}/g, getReporterName())
      .replace(/{buildingId}/g, ticket.building?.id || 'het gebouw')
      .replace(/{unitId}/g, ticket.unit?.id || 'uw unit')
      .replace(/{buildingAddress}/g, buildingAddress)
      .replace(/{unitInfo}/g, unitInfo);
  };

  const getReporterName = (): string => {
    if (!ticket.reporter) return 'Geachte bewoner';
    
    const firstName = (ticket.reporter as any).firstName || '';
    const lastName = (ticket.reporter as any).lastName || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    return 'Geachte bewoner';
  };

  const generateContactAndInvoiceBlock = (): string => {
    const reporter = ticket.reporter as any;
    const building = ticket.building as any;
    const unit = ticket.unit as any;
    const company = building?.company;

    // A) Contact ter plaatse
    const reporterName = reporter
      ? `${reporter.firstName || ''} ${reporter.lastName || ''}`.trim() || '—'
      : '—';
    const reporterPhone = reporter?.phone_mobile || reporter?.phone_direct || '—';
    const reporterEmail = reporter?.email || '—';

    // B) Vennootschapsgegevens
    const companyName = company
      ? `${company.name || ''}${company.legalForm ? ' ' + company.legalForm : ''}`
      : '—';
    const companyAddress = company?.googlePlaceLongAddress || '—';
    const companyKbo = company?.kbo || '—';
    const companyVat = company?.vat || '—';

    // C) Ticket nummer
    const ticketRef = `H-${ticket.id}`;

    // D) Adres interventieplaats
    const buildingAddress = building?.googlePlaceLongAddress || building?.id || '—';
    const unitId = unit?.id || '—';
    const unitBox = unit?.boxNumber || '—';
    const unitFloor = unit?.floor != null ? unit.floor : '—';

    return `
<br/><hr/>
<p><strong>[CONTACT TER PLAATSE &amp; FACTURATIEGEGEVENS]</strong></p>

<p><strong>A) Contact ter plaatse (huurder)</strong><br/>
Naam: ${reporterName}<br/>
Telefoon: ${reporterPhone}<br/>
E-mail: ${reporterEmail}</p>

<p><strong>B) Facturatiegegevens</strong><br/>
Naam: ${companyName}<br/>
Adres: ${companyAddress}<br/>
KBO: ${companyKbo}<br/>
BTW: ${companyVat}<br/>
<em>Facturatie verloopt via Peppol.</em></p>

<p><strong>C) Referentie op factuur (verplicht)</strong><br/>
⚠️ Vermeld <strong>${ticketRef}</strong> op uw factuur. Facturen zonder dit referentienummer worden niet goedgekeurd.</p>

<p><strong>D) Adres interventieplaats</strong><br/>
Adres: ${buildingAddress}<br/>
Unit: ${unitId}<br/>
Bus: ${unitBox}<br/>
Verdieping: ${unitFloor}</p>

<p>Graag een bevestiging sturen als antwoord op deze mail wanneer je de werken hebt uitgevoerd en enkele foto's bijvoegen.</p>

<p>Met vriendelijke groet,<br/>
Ella van Yally</p>`;
  };

  const handleInsertContactBlock = () => {
    const block = generateContactAndInvoiceBlock();
    setContent(prev => (prev || '') + block);
  };

  const stripCidReferences = (html: string): string => {
    if (!html) return html;
    // Remove img tags with cid: src
    let stripped = html.replace(/<img[^>]*src=["']cid:[^"']*["'][^>]*\/?>/gi, '');
    // Remove any remaining src="cid:..." attributes
    stripped = stripped.replace(/src=["']cid:[^"']*["']/gi, 'src=""');
    return stripped;
  };

  const buildQuotedContent = (body: string, from: string, date: string): string => {
    try {
      const cleanBody = stripCidReferences(body);
      const formattedDate = new Date(date).toLocaleString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
      return `<br><br><div style="border-left: 3px solid #ccc; padding-left: 12px; margin-top: 16px; color: #666;"><p style="margin-bottom: 8px;"><strong>Op ${formattedDate} schreef ${from}:</strong></p><div>${cleanBody}</div></div>`;
    } catch (error) {
      console.error('Error building quoted content:', error);
      return '';
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset AI summary flag when modal opens
      setAiSummaryLoaded(false);

      if (replyToSubject) {
        const ticketIdCode = isInsurance
          ? `INS${insuranceIssueId || ticket.id}`
          : communicationMode === 'supplier'
            ? `HS${ticket.id}`
            : `H${ticket.id}`;
        let replySubject = replyToSubject;

        if (!replySubject.toLowerCase().startsWith('re:')) {
          replySubject = `RE: ${replySubject}`;
        }

        if (!replySubject.includes(ticketIdCode)) {
          replySubject = `${replySubject} [${ticketIdCode}]`;
        }

        setSubject(replySubject);

        // If replying to a specific email, pre-fill content with quoted block
        if (replyToBody && replyToFrom && replyToDate) {
          const quotedBlock = buildQuotedContent(replyToBody, replyToFrom, replyToDate);
          setContent(quotedBlock);
        }
      } else {
        const defaultSubject = isInsurance
          ? `Verzekeringsdossier INS${insuranceIssueId || ticket.id} - ${ticket.title}`
          : communicationMode === 'supplier'
            ? `Ticket HS${ticket.id} - ${ticket.title}`
            : `Ticket H${ticket.id} - ${ticket.title}`;
        setSubject(defaultSubject);

        // Auto-select AI summary template for first-time supplier communication (only if no existing emails)
        if (communicationMode === 'supplier' && !replyToSubject && !hasExistingEmails) {
          setSelectedTemplate('supplier_ai_summary');
        }
      }
    }
  }, [isOpen, replyToSubject, replyToBody, replyToFrom, replyToDate, ticket.id, ticket.title, isInsurance, insuranceIssueId, communicationMode, hasExistingEmails]);

  // Auto-load AI summary when template is selected for supplier communication
  useEffect(() => {
    if (selectedTemplate === 'supplier_ai_summary' && communicationMode === 'supplier' && isOpen && !replyToSubject && !aiSummaryLoaded) {
      setAiSummaryLoaded(true);
      handleTemplateSelect('supplier_ai_summary');
    }
  }, [selectedTemplate, communicationMode, isOpen, aiSummaryLoaded]);

  const handleTemplateSelect = async (templateId: string) => {
    const templateArray = isInsurance 
      ? EMAIL_TEMPLATES_INSURANCE 
      : communicationMode === 'supplier' 
        ? EMAIL_TEMPLATES_SUPPLIER 
        : EMAIL_TEMPLATES;
        
    const template = templateArray.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      
      // Special handling for AI summary template
      if (templateId === 'supplier_ai_summary' && communicationMode === 'supplier') {
        setIsSending(true);
        try {
          const response = await fetchWithAuth(
            `${API_URL}/helpdesk/ticket/${ticket.id}/generate-supplier-summary`,
            { method: 'GET' },
            session
          );
          
          if (response.ok) {
            const aiSummary = await response.json();
            setSubject(aiSummary.subject);
            // Convert plain text to HTML for WYSIWYG editor
            const htmlContent = aiSummary.content
              .replace(/\n\n/g, '</p><p>')  // Double newlines = new paragraphs
              .replace(/\n/g, '<br>')       // Single newlines = line breaks
              .replace(/^/, '<p>')          // Start with paragraph
              .replace(/$/, '</p>');        // End with paragraph
            setContent(htmlContent);
          } else {
            throw new Error('Failed to generate AI summary');
          }
        } catch (error) {
          console.error('Error generating AI summary:', error);
          toast.error('Fout bij genereren AI samenvatting');
          // Fall back to template
          let templateSubject = replaceTemplateVariables(template.subject);
          if (replyToSubject && !templateSubject.toLowerCase().startsWith('re:')) {
            templateSubject = `RE: ${templateSubject}`;
          }
          setSubject(templateSubject);
          setContent(replaceTemplateVariables(template.content));
        } finally {
          setIsSending(false);
        }
      } else {
        let templateSubject = replaceTemplateVariables(template.subject);
        
        if (replyToSubject && !templateSubject.toLowerCase().startsWith('re:')) {
          templateSubject = `RE: ${templateSubject}`;
        }
        
        setSubject(templateSubject);
        setContent(replaceTemplateVariables(template.content));
      }
    }
  };

  const handleSendEmail = async () => {
    if (!subject.trim() || !content.trim()) {
      toast.error("Onderwerp en inhoud zijn verplicht");
      return;
    }

    // Validation based on communication type
    if (isInsurance) {
      if (!insuranceIssueId) { toast.error('Geen dossier'); return; }
      if (!Array.isArray(insuranceRecipients) || insuranceRecipients.length === 0) { toast.error('Geen ontvangers geselecteerd'); return; }
    } else if (communicationMode === 'supplier') {
      const primarySupplier = getPrimaryTicketSupplier(ticket);
      if (!primarySupplier || !primarySupplier.email) {
        toast.error("Geen leverancier met e-mailadres gevonden voor dit ticket");
        return;
      }
    } else {
      if (!ticket.reporter || !(ticket.reporter as any).email) {
        toast.error("Geen e-mailadres gevonden voor de melder");
        return;
      }
    }

    setIsSending(true);
    
    try {
      // Upload attachments first if any exist
      let uploadedAttachments: any[] = [];
      if (attachedFiles.length > 0) {
        const formData = new FormData();
        attachedFiles.forEach(attachedFile => {
          formData.append('files', attachedFile.file);
        });

        const uploadResponse = await fetchWithAuth(
          `${API_URL}/email/upload-attachments`,
          {
            method: "POST",
            body: formData,
          },
          session
        );

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          uploadedAttachments = uploadResult.attachments;
        } else {
          toast.error("Fout bij uploaden bijlagen");
          return;
        }
      }

      let apiUrl: string;
      let requestBody: any;

      if (isInsurance) {
        apiUrl = `${API_URL}/insurance/file/${insuranceIssueId}/send-email`;
        requestBody = { to: insuranceRecipients, subject, content, attachments: uploadedAttachments, replyToMessageId };
      } else if (communicationMode === 'supplier') {
        apiUrl = `${API_URL}/helpdesk/ticket/${ticket.id}/send-supplier-email`;
        requestBody = { subject, content, attachments: uploadedAttachments, replyToMessageId };
      } else {
        apiUrl = `${API_URL}/helpdesk/ticket/${ticket.id}/send-email`;
        requestBody = { to: (ticket.reporter as any).email, subject, content, reporterName: getReporterName(), attachments: uploadedAttachments, replyToMessageId };
      }

      const response = await fetchWithAuth(
        apiUrl,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
        session
      );

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      toast.success("E-mail succesvol verzonden!");
      onClose();
      onSuccess?.();
      
      // Reset form
      setSelectedTemplate("");
      setSubject("");
      setContent("");
      
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Fout bij verzenden van e-mail");
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate("");
    setSubject("");
    setContent("");
    setAiSummaryLoaded(false);
    // Clean up file attachments
    attachedFiles.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
    setAttachedFiles([]);
    setIsDragOver(false);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[90vh]",
        body: "p-0"
      }}
    >
      <ModalContent>
        <ModalHeader className="flex items-center justify-between bg-white border-b border-neutral-primary/10 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-8 bg-brick-primary rounded-full"></div>
            <div>
              <h2 className="text-xl font-semibold text-neutral-primary">
                {isInsurance ? `Snelle reactie - Verzekeringsdossier INS${insuranceIssueId || ticket.id}` : `Snelle reactie - Ticket #${ticket.id}`}
              </h2>
              <p className="text-sm text-neutral-secondary-mid">
                {ticket.title}
              </p>
            </div>
          </div>
          <Button
            isIconOnly
            variant="light"
            onPress={handleClose}
            className="text-neutral-secondary-mid hover:text-brick-primary"
          >
            <X size={20} />
          </Button>
        </ModalHeader>

        <ModalBody className="p-6 space-y-6">
          {/* Recipient info for supplier/tenant communication */}
          {!isInsurance && (
            <div className="bg-neutral-primary/5 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-neutral-primary">Aan:</span>
                {communicationMode === 'supplier' ? (
                  <span className="text-sm text-neutral-primary">
                    {getPrimaryTicketSupplier(ticket)?.email || 'Geen leverancier email'} (via repairs@yally.be)
                  </span>
                ) : (
                  <span className="text-sm text-neutral-primary">
                    {(ticket.reporter as any)?.email || 'Geen melder email'} (via helpdesk@yally.be)
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Info blok */}
          <div className="bg-brick-secondary-fine/30 rounded-lg p-4">
            <div className="flex flex-wrap gap-2 mb-2">
              <Chip
                size="sm"
                className="bg-moss-primary/10 text-moss-primary border border-moss-primary"
              >
                {ticket.building?.id}
              </Chip>
              {ticket.unit && (
                <Chip
                  size="sm"
                  className="bg-moss-primary/10 text-moss-primary border border-moss-primary"
                >
                  {ticket.unit.id}
                </Chip>
              )}
              {!isInsurance && communicationMode === 'tenant' && (
                <Chip size="sm" className="bg-brick-primary/10 text-brick-primary border border-brick-primary">{getReporterName()}</Chip>
              )}
              {!isInsurance && communicationMode === 'supplier' && getPrimaryTicketSupplier(ticket) && (
                <Chip size="sm" className="bg-moss-primary/10 text-moss-primary border border-moss-primary">
                  {getPrimaryTicketSupplier(ticket)?.name || 'Leverancier'}
                </Chip>
              )}
              {isInsurance && Array.isArray(insuranceRecipients) && insuranceRecipients.length > 0 && (
                <Chip size="sm" className="bg-blue-100 text-blue-800 border border-blue-300">
                  Ontvangers: {insuranceRecipients.join(', ')}
                </Chip>
              )}
            </div>
          </div>

          {/* Template Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-primary mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Kies een sjabloon (optioneel)
            </label>
            <Select
              placeholder="Selecteer een sjabloon"
              value={selectedTemplate}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              classNames={{
                trigger: "border-neutral-primary/20 hover:border-brick-primary"
              }}
            >
              {(isInsurance 
                ? EMAIL_TEMPLATES_INSURANCE 
                : communicationMode === 'supplier' 
                  ? EMAIL_TEMPLATES_SUPPLIER 
                  : EMAIL_TEMPLATES
              ).map((template) => (
                <SelectItem key={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-neutral-primary mb-2">
              Onderwerp
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Voer het onderwerp in"
              classNames={{
                inputWrapper: "border-neutral-primary/20 hover:border-brick-primary focus-within:border-brick-primary"
              }}
            />
          </div>

          {/* AI-antwoordvoorstel: de knop, en het voorstel dat de backend al
              schreef toen de huurdersmail binnenkwam.

              Bewust een apart blok bóven het antwoordvenster en niet meteen
              erin: zo blijft zichtbaar welk stuk tekst van de AI komt, en
              beslis je zelf of je het overneemt. Er is nog nooit iets
              verstuurd op dit punt. */}
          {!isInsurance && (
            <AiReplySuggestionPanel
              suggestion={suggestion}
              isGenerating={isGeneratingSuggestion}
              error={suggestionError}
              instruction={suggestionInstruction}
              onInstructionChange={setSuggestionInstruction}
              onResetInstruction={resetSuggestionInstruction}
              canResetInstruction={isSuggestionInstructionModified}
              onGenerate={requestSuggestion}
              onApply={applySuggestion}
              onDismiss={dismissSuggestion}
            />
          )}

          {/* Content Editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-neutral-primary">
                Bericht
              </label>
              {communicationMode === 'supplier' && (
                <Button
                  size="sm"
                  variant="light"
                  onPress={handleInsertContactBlock}
                  startContent={<ClipboardText size={15} />}
                  className="text-moss-primary hover:bg-moss-primary/10 text-xs"
                >
                  Contact & facturatiegegevens invoegen
                </Button>
              )}
            </div>
            <div className="border border-neutral-primary/20 rounded-lg overflow-hidden">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={setContent}
                modules={emailEditorModules}
                placeholder="Typ uw bericht hier..."
                style={{
                  height: '300px',
                  backgroundColor: 'white'
                }}
              />
            </div>
            {/* Add spacing after the editor */}
            <div className="h-12"></div>
          </div>

          {/* File Attachments Section */}
          <div className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-neutral-primary flex items-center gap-2">
                <Paperclip size={16} />
                Bijlagen toevoegen
                {attachedFiles.length > 0 && (
                  <span className="text-xs bg-moss-primary/10 text-moss-primary px-2 py-1 rounded-full">
                    {attachedFiles.length} bestand{attachedFiles.length !== 1 ? 'en' : ''}
                  </span>
                )}
              </label>
              <div className="flex items-center gap-2">
                {attachedFiles.length > 0 && (
                  <span className="text-xs text-neutral-secondary-mid">
                    Totaal: {formatFileSize(getTotalAttachmentSize())}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="light"
                  onPress={() => fileInputRef.current?.click()}
                  startContent={<Paperclip size={14} />}
                  className="text-moss-primary hover:bg-moss-primary/10"
                >
                  Bestand selecteren
                </Button>
              </div>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={FILE_INPUT_ACCEPT}
              onChange={handleFileInputChange}
              className="hidden"
            />

            {/* Drag and drop zone */}
            <div
              onDrop={handleFileDrop}
              onDragOver={handleFileDragOver}
              onDragLeave={handleFileDragLeave}
              className={`
                border-2 border-dashed rounded-lg p-6 text-center transition-colors
                ${isDragOver 
                  ? 'border-moss-primary bg-moss-primary/5' 
                  : 'border-neutral-300 hover:border-moss-primary/50'
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <Paperclip size={24} className="text-neutral-400" />
                <p className="text-sm text-neutral-600">
                  Sleep bestanden hierheen of{' '}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-moss-primary hover:underline"
                  >
                    klik om te selecteren
                  </button>
                </p>
                <p className="text-xs text-neutral-400">
                  Max 25MB per bestand, 100MB totaal
                </p>
              </div>
            </div>

            {/* Attached Files List */}
            {attachedFiles.length > 0 && (
              <div className="space-y-2">
                <h5 className="text-sm font-medium text-neutral-primary">Toegevoegde bestanden:</h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {attachedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-3 bg-neutral-50 border border-neutral-200 rounded-lg"
                    >
                      {/* File Icon/Preview */}
                      <div className="shrink-0">
                        {file.previewUrl ? (
                          <img
                            src={file.previewUrl}
                            alt={file.name}
                            className="w-10 h-10 object-cover rounded-sm border border-neutral-300"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-neutral-200 rounded-sm border border-neutral-300 flex items-center justify-center">
                            <File size={20} className="text-neutral-600" />
                          </div>
                        )}
                      </div>

                      {/* File Info */}
                      <div className="grow min-w-0">
                        <p className="text-sm font-medium text-neutral-primary truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>

                      {/* Remove button */}
                      <Button
                        size="sm"
                        variant="light"
                        isIconOnly
                        onPress={() => handleRemoveFile(file.id)}
                        className="text-red-500 hover:bg-red-50"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="border-t border-neutral-primary/10 px-6 py-4">
          <Button
            variant="light"
            onPress={handleClose}
            className="text-neutral-secondary-mid"
          >
            Annuleren
          </Button>
          <Button
            color="primary"
            onPress={handleSendEmail}
            isLoading={isSending}
            startContent={!isSending && <PaperPlaneRight />}
            className="bg-moss-primary text-white"
          >
            {isSending ? "Verzenden..." : "Verstuur e-mail"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
